// This comes straight from an example on the react-final-form docs website.
// Takes multiple given validator functions and creates a function that takes a
// value and returns the first error message received from one of the validators.
export const composeValidators = (...validators) => value =>
    validators.reduce(
        (error, validator) => error || validator(value),
        undefined,
    )

/** Create a form validator for a form by supplying a list of required fields.
 *
 * @param requiredFields Array of strings corresponding to properties on the values object that will be expected for the returned function.
 */
export const createValidator = (requiredFields = []) => values => {
    const errors = {}

    // Check all required fields are there
    requiredFields.forEach(field => {
        if (!values[field]) {
            errors[field] = 'Required'
        }
    })

    // Email
    const emailFormat = /^.+@.+\..{1,63}$/i
    if (
        values.email !== undefined &&
        !errors.email &&
        !emailFormat.test(values.email.trim())
    ) {
        errors.email = 'Invalid email address'
    }

    // Text Notifications
    if (
        values.textNotifications !== undefined &&
        values.textNotifications == 1
    ) {
        if (!values.phone) {
            errors.phone = 'Required'
        }
    }

    // Phone Number
    const phoneNumberFormat = /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i
    if (
        values.phone !== undefined &&
        !errors.phone &&
        !phoneNumberFormat.test(values.phone) &&
        values.phone != '' && // left as-is for now, should be strict inequality
        values.phone !== null &&
        values.phone.length > 2
    ) {
        errors.phone = 'Phone number must be 10 digits'
    }

    // Password
    if (
        values.password !== undefined &&
        !errors.password &&
        values.password.length < 6
    ) {
        errors.password = 'Password minimum is 6 characters'
    }

    // New Password (should match Password)
    if (
        values.new_password !== undefined &&
        !errors.new_password &&
        values.new_password.length < 6
    ) {
        errors.new_password = 'Password minimum is 6 characters'
    }

    // Password Confirmation
    if (
        values.password_confirmation !== undefined &&
        !errors.password_confirmation &&
        values.password_confirmation.length < 6
    ) {
        errors.password_confirmation = 'Password minimum is 6 characters'
    }

    // New Password Confirmation (should match Password Confirmation)
    if (
        values.new_password_confirmation !== undefined &&
        !errors.new_password_confirmation &&
        values.new_password_confirmation.length < 6
    ) {
        errors.new_password_confirmation = 'Password minimum is 6 characters'
    }

    return errors
}

// Validation for the used equipment form
export const validateUsedEquipmentSearch = (
    selectedCategory,
    fuelType,
    minCapacity,
    maxCapacity,
    minHeight,
    maxHeight,
) => {
    if (selectedCategory == 'forklifts') {
        const min = parseInt(minCapacity)
        if (!min) {
            return 'Min capacity must be a valid number'
        }
        const max = parseInt(maxCapacity)
        if (!max) {
            return 'Max capacity must be a valid number'
        }
        if (min > max) {
            return 'Min capacity must be lower lower than the max capacity'
        }

        if (fuelType == '') {
            return 'Fuel type must be valid'
        }
    }

    if (selectedCategory == 'aerialLifts') {
        const min = parseInt(minHeight)
        if (!min) {
            return 'Min height must be a valid number'
        }
        const max = parseInt(maxHeight)
        if (!max) {
            return 'Max height must be a valid number'
        }
        if (min > max) {
            return 'Min height must be lower lower than the max height'
        }
    }

    return ''
}

// Enum object for statuses
export const Status = {
    INIT: 'init',
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
}

// Create a string with a given number of zeroes.
const zeroes = num => {
    let zeroes = ''
    for (let i = 1; i <= num; i++) {
        zeroes += '0'
    }
    return zeroes
}

/**
 * Add zeroes to get a 10 character string from an ID. Return ID's that are
 * already 10 characters or longer. Error on anything but a string or number.
 *
 * @param {string, number} id
 */
export const padIdWithZeroes = id => {
    if (typeof id !== 'string' && typeof id !== 'number') {
        throw new TypeError('Not a string or number.')
    }
    if (typeof id === 'number' && id !== Math.round(id)) {
        return new TypeError("Number ID can't contain a decimal.")
    }

    const stringId = typeof id === 'number' ? id.toString(10) : id

    if (stringId.length >= 10) {
        return stringId
    } else {
        const neededQty = 10 - stringId.length
        return zeroes(neededQty) + stringId
    }
}

/**
 * Add commas and spaces to format address parts into a single line address.
 * Allow for missing parts to the address.
 *
 * @param {string} street
 * @param {string} city
 * @param {string} stateCode
 * @param {string} zip
 */
export const singleLineAddress = (street, city, stateCode, zip) => {
    let cityStatePart = ''

    if (city && stateCode) {
        cityStatePart += city + ', ' + stateCode
    } else if (city) {
        cityStatePart += city
    } else if (stateCode) {
        cityStatePart += stateCode
    }

    let locationPart = ''
    if (street && cityStatePart) {
        locationPart += street + ', ' + cityStatePart
    } else if (street) {
        locationPart += street
    } else if (cityStatePart) {
        locationPart += cityStatePart
    }

    let address = 'No address listed'
    if (locationPart && zip) {
        address = locationPart + ' ' + zip
    } else if (locationPart) {
        address = locationPart
    } else if (zip) {
        address = zip
    }

    return address
}

// The browser checks below are from this Stack Overflow post:
// https://stackoverflow.com/questions/49328382/browser-detection-in-reactjs
/** Check for Internet Explorer 6-11 */
export const isInternetExplorer = () =>
    /*@cc_on!@*/ false || !!document.documentMode

/** Check for Safari 3.0+ */
export const isSafari = () =>
    /constructor/i.test(window.HTMLElement) ||
    (function(p) {
        return p.toString() === '[object SafariRemoteNotification]'
    })(
        !window['safari'] ||
            (typeof window.safari !== 'undefined' &&
                window.safari != null &&
                window.safari.pushNotification),
    )

/** Add a text tag to the Hotjar recording for easy filtering.
 *
 * @param tag String with a short description of what is tag-worthy.
 */
export const tagHotjarRecording = tag => {
    window.hj =
        window.hj ||
        function() {
            ;(window.hj.q = window.hj.q || []).push(arguments)
        }
    window.hj('tagRecording', [tag])
}

/** Send an Intercom event tag for recording user interactions in Intercom.
 *
 * @param eventName String with a short description of what the user did.
 */
export const sendEventToIntercom = eventName => {
    if (typeof window.Intercom === 'function') {
        window.Intercom('trackEvent', eventName)
    }
}

export default {
    // version from `meta.json` - first param
    // version in bundle file - second param
    semverGreaterThan: (versionA, versionB) => {
        // Add null checks to prevent errors on undefined or null versions
        if (!versionA || !versionB) return false

        const versionsA = versionA.split(/\./g)
        const versionsB = versionB.split(/\./g)

        while (versionsA.length || versionsB.length) {
            const a = Number(versionsA.shift())
            const b = Number(versionsB.shift())
            // eslint-disable-next-line no-continue
            if (a === b) continue
            // eslint-disable-next-line no-restricted-globals
            return a > b || isNaN(b)
        }
        return false
    },
}

/**
 * Determine whether a user is activated or not. (I.e. whether they have a Salesforce ID assigned.)
 *
 * @param {object} user User object with external_contact_id property (string | null, Salesforce ID)
 */
export const isActive = user => !!user.external_contact_id

/**
 * Determine whether a user has been invited or not. (sent invite or activation emails)
 *
 * @param {object} user User object with invites property (array of invites' data)
 */
export const wasInvited = user =>
    Array.isArray(user.invites) && user.invites.length > 0

/**
 * Format the numbers to local US phone number
 *
 * @param {number || string} user Phone number with 10 digits to format
 */
export const formatPhoneNumberLocalUS = phoneNumber => {
    if (!phoneNumber) {
        return ''
    }
    return `${phoneNumber?.slice(0, 3)}-${phoneNumber?.slice(
        3,
        6,
    )}-${phoneNumber?.slice(6, 10)}`
}

/**
 * Format the numbers to local US phone number
 *
 * @param {number || string} user Phone number with 10 digits to format
 */
export const formatPhoneToInput = value => {
    if (value !== null) {
        return `(${value?.slice(0, 3)}) ${value?.slice(3, 6)}-${value?.slice(
            6,
            10,
        )}`
    }
}

export const formatPhoneString = phoneNumber => {
    if (phoneNumber !== null) {
        return phoneNumber?.replace('+1', '')?.replace(/[^0-9]/g, '')
    }
}

/**
 * Checks user feature flags
 *
 * @param {object} user User object with an array of features available for the user
 * @param {string} feature Feature that we are checking
 */
export const featureFlag = (user, feature) =>
    user.feature_flags.find(obj => {
        return obj.feature == feature
    })

/**
 * Fucntions to endonde and decode the serial number of the equipment
 * to fix the url issues when viewing the equipment
 *
 * The function has two replacements because of the function
 * encodeURIComponent has an issue with the '/' character
 * and Laravel doesn't recognizes it
 *
 * @param {string} equipmentId Serial number of the equipment
 * @returns
 */
export const encodeEquipmentId = equipmentId => {
    let charTo = '/'
    let charFrom = '!~'
    let replaced = equipmentId.replaceAll(charTo, charFrom)
    return encodeURIComponent(replaced)
}

export const decodeEquipmentId = equipmentId => {
    let charTo = '!~'
    let charFrom = '/'
    let replaced = equipmentId.replaceAll(charTo, charFrom)
    return decodeURIComponent(replaced)
}

export const getCategoryCards = () => {
    return [
        {
            name: 'All Classes',
            category: 'allClasses',
            class: 'allClasses',
            equipmentType: '',
            equipmentSubtype: '',
            showFuelTypeAndCapacity: false,
            showHeight: false,
        },
        {
            name: 'Aerial Lifts',
            category: 'aerialLifts',
            class: 'aerialLifts',
            showFuelTypeAndCapacity: true,
            showHeight: false,
        },
        {
            name: 'Automations',
            category: 'automations',
            class: 'automations',
            showFuelTypeAndCapacity: true,
            showHeight: false,
        },
        // {
        //     name: 'Batteries',
        //     category: 'batteries',
        //     class: 'batteries',
        //     showFuelTypeAndCapacity: false,
        //     showHeight: false,
        // },
        // {
        //     name: 'Chargers',
        //     category: 'chargers',
        //     class: 'chargers',
        //     showFuelTypeAndCapacity: false,
        //     showHeight: false,
        // },
        {
            name: 'Forklifts',
            category: 'forklifts',
            class: 'forklifts',
            showFuelTypeAndCapacity: true,
            showHeight: false,
        },
        {
            name: 'Pallet Trucks',
            category: 'palletTrucks',
            class: 'palletTrucks',
            showFuelTypeAndCapacity: true,
            showHeight: false,
        },
        {
            name: 'Rail and Transit',
            category: 'railAndTransit',
            class: 'railAndTransit',
            showFuelTypeAndCapacity: true,
            showHeight: false,
        },
        {
            name: 'Warehouse Vehicles and Operated Machinery',
            category: 'warehouseVehiclesAndOperatedMachinery',
            class: 'warehouseVehiclesAndOperatedMachinery',
            showFuelTypeAndCapacity: true,
            showHeight: false,
        },
        {
            name: 'Warehouse Accessories',
            category: 'warehouseAccessories',
            class: 'warehouseAccessories',
            showFuelTypeAndCapacity: true,
            showHeight: false,
        },
    ]
}

export const getTireGroupValues = () => {
    return [
        { key: 'CUSHION', label: 'Cushion', value: 'CUSHION' },
        {
            key: 'CUSHION NON-MARKING',
            label: 'Cushion Non-Marking',
            value: 'CUSHION NON-MARKING',
        },
        {
            key: 'PNEUMATIC SOLID',
            label: 'Pneumatic Solid',
            value: 'PNEUMATIC SOLID',
        },
        {
            key: 'PNEUMATIC AIR',
            label: 'Pneumatic Air',
            value: 'PNEUMATIC AIR',
        },
    ]
}

export const getForkLength = () => {
    return [
        { key: 36, label: '36 in', value: 36 },
        { key: 42, label: '42 in', value: 42 },
        { key: 48, label: '48 in', value: 48 },
        { key: 54, label: '54 in', value: 54 },
        { key: 60, label: '60 in', value: 60 },
        { key: 72, label: '72 in', value: 72 },
        { key: 96, label: '96 in', value: 96 },
        { key: 120, label: '120 in', value: 120 },
    ]
}

export const createGroups = (arr, numGroups) => {
    const perGroup = Math.ceil(arr.length / numGroups)
    if (arr?.length === 0) {
        return []
    }
    return new Array(numGroups)
        .fill('')
        .map((_, i) => arr.slice(i * perGroup, (i + 1) * perGroup))
}

export const getAssetPageTabDefault = () => {
    return [
        { key: 'overviewTab', active: false },
        { key: 'serviceHistoryTab', active: false },
        { key: 'plannedMaintenanceTab', active: false },
    ]
}

export const getRequestPageTabDefault = () => {
    return [
        { key: 'activeRequests', active: false },
        { key: 'pastRequests', active: false },
        { key: 'estimateRequests', active: false },
    ]
}

export const getPlannedMaintenancePageTabDefault = () => {
    return [
        { key: 'activeRequests', active: false },
        { key: 'pastRequests', active: false },
    ]
}
